import React, { useEffect, useMemo } from 'react';

import { contactApis } from '@apis/next/contactApis';
import { zipcodeCityApis } from '@apis/next/zipcodeCityApis';
import { Button } from '@components/Button';
import { FormTitle } from '@components/Forms/FormTitle';
import { RequiredTag } from '@components/Forms/RequiredTag';
import { DebugForm } from '@components/NextForms/DebugForm';
import { ErrorMessages } from '@components/NextForms/ErrorMessages';
import { ContactDisplayEditCard } from '@components/utilities/Contact/ContactDisplayEditCard';
import { VerticalSpace } from '@components/VerticalSpace';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { useFormUtils } from '@hooks/useFormUtils';
import type { ContactType } from '@models/Contact';
import {
  type BaseContactAddForm,
  ContactAddFormCN,
  ContactAddFormJP,
  // exampleContactAddFormCN,
} from '@models/next/Contact';
import { empty, getDecodeResult, getDecodeResultOr } from '@monoid-dev/mobx-zod-form';
import { type ReactForm, useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { stringifySearch, toggleArray } from '@utils/types';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import { pipe } from 'remeda';

const strings = createStrings({
  ja: {
    contactInfo: (category: ContactType) => `${bookingInquiryStrings[category]}情報`,
    addContact: (category: ContactType) => `+${bookingInquiryStrings[category]}を追加`,
    submit: '登録',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  contactDisplayEditArray: {
    ...common.flexColumn,
    flex: 1,
  },
  ownedContactList: {
    ...common.flexColumn,
    rowGap: '1rem',
  },
  addOwnedContactPrompt: {
    ...common.middleTextImportant,
    ...common.textGray700,
    padding: '11px 8px',
    backgroundColor: theme.palette.base[200],
  },
  requiredTag: {
    marginLeft: '1rem',
  },
  errorMessage: {
    marginBottom: '1rem',
  },
}));

interface ContactAddFormProps {
  contactType: ContactType;
  ownedContactType: ContactType;
  sameAsOwnerDisabled?: boolean;
}

const OwnedContactArray: React.FC<
  ContactAddFormProps & { form: ReactForm<typeof BaseContactAddForm> }
> = observer(({ form, ownedContactType, sameAsOwnerDisabled }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { fields } = form.root;

  const sameAsOwnerIdsValue = getDecodeResultOr(fields.sameAsOwnerIds.decodeResult, [] as number[]);

  return (
    <>
      <div className={classes.ownedContactList}>
        {fields.ownedContacts.elements.map((field, i) => (
          <ContactDisplayEditCard
            key={field.uniqueId}
            item={{
              action: 'create',
              mode: sameAsOwnerIdsValue.includes(field.uniqueId) ? 'sameAsOwner' : 'edit',
              id: field.uniqueId,
            }}
            ownerAction="create"
            field={field}
            type={ownedContactType}
            ownedContactIndex={i}
            onChangeSameAsOwner={() =>
              fields.sameAsOwnerIds.setOutput(toggleArray(sameAsOwnerIdsValue, field.uniqueId))
            }
            onDelete={() => fields.ownedContacts.splice(i, 1, [])}
            sameAsOwnerDisabled={sameAsOwnerDisabled}
          />
        ))}

        <span
          className={clsx(classes.link, classes.baseButton, classes.middleText)}
          onClick={() => fields.ownedContacts.push(empty)}
        >
          {strings.addContact(ownedContactType)}
        </span>
      </div>

      <ErrorMessages field={fields.ownedContacts} />
    </>
  );
});

export const ContactAddForm: React.FC<ContactAddFormProps> = observer((props) => {
  const { contactType, ownedContactType } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const postContact = contactApis.usePost('/contact');

  const { renderAlertsAndSpinnerModal } = useFormUtils({ mutations: [postContact] });

  const history = useHistory();

  /**
   * We know the two forms are the same for Form anyways
   */
  const form = useForm(
    (contactType === 'shipper' ? ContactAddFormCN : ContactAddFormJP) as typeof BaseContactAddForm,
    {
      initialOutput: empty,
      plugins: [
        {
          name: 'fill-same-owned-contacts',
          onBeforeSubmit() {
            fields.ownedContacts.elements.forEach((field) => {
              if (getDecodeResult(fields.sameAsOwnerIds.decodeResult)?.includes(field.uniqueId)) {
                const contactValue = getDecodeResult(fields.contact.decodeResult);
                if (contactValue) {
                  field.setOutput(contactValue);
                }
              }
            });
          },
        },
      ],
    },
  );

  const { fields } = form.root;

  const zipcode = pipe(getDecodeResultOr(fields.contact.fields.zip_code.decodeResult, ''), (v) =>
    useDebouncedValue(() => v, 100, [v]),
  );

  const cityByZipcodeQuery = zipcodeCityApis.useGet(
    '/zipcode-city/city/:zipcode',
    {
      params: {
        zipcode,
      },
    },
    {
      enabled: !!zipcode && contactType === 'shipper',
      keepPreviousData: true,
    },
  );

  const validCNZipcode = useMemo(
    () => cityByZipcodeQuery.data?.length || 0 >= 1,
    [cityByZipcodeQuery.data],
  );

  useEffect(() => {
    if (contactType === 'shipper' && !validCNZipcode) {
      fields.sameAsOwnerIds.setOutput([]);
    }
  }, [validCNZipcode]);

  return (
    <form
      className={classes.contactDisplayEditArray}
      {...form.bindForm({
        async onSubmit(values) {
          const contact = await postContact.mutateAsync({
            ...values.contact,
            social_credit_id: values.contact.social_credit_id ?? '',
            email: values.contact.email ?? '',
            contact_type: contactType,
          });

          // We don't need to copy the contact for owned contacts which are in sameAsOwner,
          // because it is handled in `onClick`.
          for (const owned of values.ownedContacts) {
            await postContact.mutateAsync({
              ...owned,
              social_credit_id: values.contact.social_credit_id ?? '',
              email: owned.email ?? '',
              contact_type: ownedContactType,
              owner: contact.id,
            });
          }

          if (contactType === 'shipper') {
            history.replace(
              `${url('/utilities/Contact/Shippers/ShipperDetails')}?${stringifySearch({
                id: contact.id,
                action: 'createSuccess',
              })}`,
            );
          } else {
            history.replace(
              `${url('/utilities/Contact/Consignees/ConsigneeDetails')}?${stringifySearch({
                id: contact.id,
                action: 'createSuccess',
              })}`,
            );
          }
        },
      })}
    >
      {renderAlertsAndSpinnerModal()}

      <FormTitle title={strings.contactInfo(contactType)} />
      <VerticalSpace height={8} />

      <ContactDisplayEditCard
        field={fields.contact}
        item={{
          action: 'create',
          mode: 'edit',
          id: 1,
        }}
        ownerAction="create"
        type={contactType}
      />

      <VerticalSpace height={32} />
      <FormTitle
        title={
          <>
            {strings.contactInfo(ownedContactType)}
            {ownedContactType === 'receiver' && <RequiredTag className={classes.requiredTag} />}
          </>
        }
      />

      <OwnedContactArray
        {...props}
        form={form}
        sameAsOwnerDisabled={contactType === 'shipper' && !validCNZipcode}
      />

      <VerticalSpace height={16} />

      <Button
        color="primary"
        text={strings.submit}
        size="small"
        style={{ width: 88 }}
        type="submit"
      />

      <DebugForm form={form} />
    </form>
  );
});
